import { useEffect, useRef } from 'react';
import { sendControl, type ControlMessage } from './protocol';

interface UseClipboardSyncOpts {
  controlChannel: RTCDataChannel | null;
  enabled: boolean;
}

/**
 * Bidirectional clipboard text sync over the `control` DataChannel.
 *
 * Outbound (browser → host):
 *   - Polls `navigator.clipboard.readText()` every 1.0s when the document has focus AND
 *     `document.hasFocus()` AND `navigator.clipboard` is available.
 *   - Sends the new text via `sendControl(... ClipboardText)` only if it differs from the most
 *     recent value we have observed (either sent OR received). This is the anti-echo invariant.
 *
 * Inbound (host → browser):
 *   - Subscribes to `controlChannel.message`. On `ClipboardText`, writes via
 *     `navigator.clipboard.writeText` and stores the text as "last known" so the next poll
 *     doesn't bounce it back to the host.
 *
 * Permissions: Chrome / Edge gate `clipboard.readText` behind a user-gesture/permission prompt.
 * On first poll attempt the browser may show an info bar or silently reject. We swallow errors
 * and retry next tick — eventual user grant unblocks the loop automatically.
 *
 * iOS Safari: clipboard.readText is blocked unless triggered by a user gesture. The poll path
 * silently fails on iOS; the inbound direction still works.
 */
export function useClipboardSync({ controlChannel, enabled }: UseClipboardSyncOpts): void {
  const channelRef = useRef(controlChannel);
  const enabledRef = useRef(enabled);
  const lastKnownRef = useRef<string>(''); // text we've either sent OR applied

  useEffect(() => {
    channelRef.current = controlChannel;
  }, [controlChannel]);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Inbound: listen on the control DC for clipboard updates from the host.
  useEffect(() => {
    if (!controlChannel) return;
    const onMessage = async (ev: MessageEvent) => {
      if (typeof ev.data !== 'string') return;
      let msg: ControlMessage;
      try {
        msg = JSON.parse(ev.data) as ControlMessage;
      } catch {
        return;
      }
      if (msg.type !== 'clipboard_text') return;
      if (msg.data === lastKnownRef.current) return; // own echo
      lastKnownRef.current = msg.data;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(msg.data);
        }
      } catch (e) {
        // Permission denied / no user gesture — swallow; user can click "paste" manually.

        console.warn('clipboard.writeText denied:', e);
      }
    };
    controlChannel.addEventListener('message', onMessage);
    return () => controlChannel.removeEventListener('message', onMessage);
  }, [controlChannel]);

  // Outbound: poll local clipboard while the tab has focus.
  useEffect(() => {
    const POLL_MS = 1000;
    let timer: number | null = null;
    const tick = async () => {
      timer = window.setTimeout(tick, POLL_MS);
      if (!enabledRef.current) return;
      if (!document.hasFocus()) return;
      if (!navigator.clipboard?.readText) return;
      try {
        const text = await navigator.clipboard.readText();
        if (text === lastKnownRef.current) return;
        lastKnownRef.current = text;
        sendControl(channelRef.current, { type: 'clipboard_text', data: text });
      } catch {
        // Read denied — quiet retry.
      }
    };
    timer = window.setTimeout(tick, POLL_MS);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
    };
  }, []);
}
